import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  CardContent,
  CircularProgress,
  Chip,
  Stack,
  Container,
  CardMedia,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  LocationOn as LocationIcon,
  Public as PublicIcon,
  AccountBalance as LocalChapterIcon,
  Link as LinkIcon,
  Share as ShareIcon,
} from '@mui/icons-material';
import { getNewsfeedById } from '../../endpoints/newsfeedTs';
import { useApiGet } from '../../utils/httpClientTs';
import { NewsFeedDetail } from '../../types/newsfeed';
import dayjs from 'dayjs';
import AlertItem from '../../Components/AlertItem';
import ReactPlayer from 'react-player';

// Content type constants
const CONTENT_TYPES = {
  paragraph: '60308536096c4ce088bee75af7ae8ae2',
  list: 'c1e257bc8c674ae28a7b2085518aea9e',
  video: 'fd9dd86a20e24e79b920e191f3b8203a',
  document: 'f0f8caf2e4934b7e86ee63a98b9b08ea', // For library documents
  bundle: '8cf9d8284bfa481087b0b7a76b690e8c', // For library bundles
};

export default function NewsFeedView(): React.JSX.Element {
  const { id } = useParams<{ id: string }>();

  // Fetch newsfeed detail from API
  const {
    data: newsfeed,
    isLoading,
    error,
  } = useApiGet<NewsFeedDetail>(
    ['newsfeed-detail', id],
    () => getNewsfeedById(id!),
    {
      enabled: !!id,
      retry: 1,
    }
  );

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: newsfeed?.title || 'PEO Newsfeed',
          text: newsfeed?.description || '',
          url: window.location.href,
        });
      } catch (error) {
        console.log('Error sharing:', error);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      // TODO: Show success toast
    }
  };

  const formatDate = (dateString: string) => {
    return dayjs(dateString).format('MMMM D, YYYY');
  };

  const getStatusColor = (isActive: boolean) => {
    return isActive ? 'success' : 'default';
  };

  const getStatusText = (isActive: boolean) => {
    return isActive ? 'Active' : 'Inactive';
  };

  // Sort sections and content by order
  const sortedSections =
    newsfeed?.sections
      ?.sort((a, b) => a.order - b.order)
      .map((section) => ({
        ...section,
        content: section.content.sort((a, b) => a.order - b.order),
      })) || [];

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !newsfeed) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <AlertItem
            severity="error"
            description="Newsfeed not found or error loading content. Please check the URL and try again."
            onClose={() => {}}
            keepOpen={true}
          />
        </Box>
      </Container>
    );
  }

  const linkCtaLabel = newsfeed.button_text?.trim() || 'Learn More';

  return (
    <Box sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
      {/* Header Section */}
      <Paper elevation={0} sx={{ mb: 4, overflow: 'hidden', borderRadius: 3 }}>
        {newsfeed.image_url && (
          <CardMedia
            component="img"
            sx={{
              maxHeight: '400px',
              width: 'auto',
              objectFit: 'contain',
              display: 'block',
              margin: '0 auto',
            }}
            image={newsfeed.image_url}
            alt={newsfeed.title}
          />
        )}
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={3} alignItems="flex-start">
            <Grid item xs={12} lg={9}>
              <Typography
                variant="h3"
                component="h1"
                gutterBottom
                sx={{ fontWeight: 700 }}
              >
                {newsfeed.title}
              </Typography>
              {newsfeed.description && (
                <Typography
                  variant="h6"
                  color="text.secondary"
                  paragraph
                  sx={{ lineHeight: 1.6 }}
                >
                  {newsfeed.description}
                </Typography>
              )}

              {/* Meta Information */}
              <Stack direction="row" spacing={2} flexWrap="wrap" sx={{ mt: 2 }}>
                <Chip
                  icon={<CalendarIcon />}
                  label={`${formatDate(newsfeed.start_date)} - ${formatDate(newsfeed.end_date)}`}
                />
                <Chip
                  icon={<PublicIcon />}
                  label={getStatusText(newsfeed.is_active)}
                  color={getStatusColor(newsfeed.is_active)}
                  variant="outlined"
                />
                {newsfeed.is_international && (
                  <Chip
                    icon={<PublicIcon />}
                    label="International"
                    color="primary"
                    variant="outlined"
                  />
                )}
                {newsfeed.local_chapter_id && (
                  <Chip
                    icon={<LocalChapterIcon />}
                    label="Local Chapter"
                    color="secondary"
                    variant="outlined"
                  />
                )}
                {newsfeed.spd_chapter_id && (
                  <Chip
                    icon={<LocationIcon />}
                    label="SPD Chapter"
                    color="secondary"
                    variant="outlined"
                  />
                )}
              </Stack>
            </Grid>

            <Grid item xs={12} lg={3}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1}>
                  <Tooltip title="Share">
                    <IconButton
                      onClick={handleShare}
                      sx={{ border: '1px solid', borderColor: 'divider' }}
                    >
                      <ShareIcon />
                    </IconButton>
                  </Tooltip>
                </Stack>

                {newsfeed.link_url && (
                  <Button
                    variant="contained"
                    startIcon={<LinkIcon />}
                    href={newsfeed.link_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    fullWidth
                    size="large"
                  >
                    {linkCtaLabel}
                  </Button>
                )}
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Paper>

      {/* Content Sections */}
      {sortedSections.length > 0 && (
        <Box sx={{ mb: 4 }}>
          {sortedSections.map((section, sectionIndex) => (
            <Paper
              key={section.id || sectionIndex}
              elevation={0}
              sx={{ mb: 3, borderRadius: 3 }}
            >
              <CardContent sx={{ p: 4 }}>
                {section.title && (
                  <Typography
                    variant="h4"
                    component="h2"
                    gutterBottom
                    sx={{ fontWeight: 600, mb: 3 }}
                  >
                    {section.title}
                  </Typography>
                )}

                <Stack spacing={1.5}>
                  {section.content.map((content, contentIndex) => (
                    <Box key={content.id || contentIndex}>
                      {/* Paragraph Content */}
                      {content.content_type_id ===
                        '60308536096c4ce088bee75af7ae8ae2' &&
                        content.paragraph && (
                          <Typography
                            variant="body1"
                            paragraph
                            sx={{ lineHeight: 1.8, fontSize: '1.1rem' }}
                          >
                            {content.paragraph}
                          </Typography>
                        )}

                      {/* List Content */}
                      {content.content_type_id ===
                        'c1e257bc8c674ae28a7b2085518aea9e' &&
                        content.list &&
                        content.title && (
                          <Box>
                            <Typography
                              variant="h6"
                              gutterBottom
                              sx={{ fontWeight: 600, mb: 2 }}
                            >
                              {content.title}
                            </Typography>
                            <Box component="ul" sx={{ pl: 3, m: 0 }}>
                              {content.list
                                .sort((a, b) => a.order - b.order)
                                .map((item, itemIndex) => (
                                  <Typography
                                    key={item.id || itemIndex}
                                    component="li"
                                    variant="body1"
                                    sx={{
                                      lineHeight: 1.8,
                                      fontSize: '1.1rem',
                                      mb: 0.5,
                                      '&:last-child': { mb: 0 },
                                    }}
                                  >
                                    {item.is_link && item.link_url ? (
                                      <Button
                                        component="a"
                                        href={item.link_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        startIcon={<LinkIcon />}
                                        sx={{
                                          textTransform: 'none',
                                          p: 0,
                                          minWidth: 'auto',
                                          color: 'primary.main',
                                          textDecoration: 'underline',
                                          '&:hover': {
                                            textDecoration: 'none',
                                          },
                                        }}
                                      >
                                        {item.content}
                                      </Button>
                                    ) : (
                                      item.content
                                    )}
                                  </Typography>
                                ))}
                            </Box>
                          </Box>
                        )}

                      {/* Video Content */}
                      {content.content_type_id ===
                        'fd9dd86a20e24e79b920e191f3b8203a' && (
                        <Box>
                          {content.title && (
                            <Typography
                              variant="h6"
                              gutterBottom
                              sx={{ fontWeight: 600, mb: 2 }}
                            >
                              {content.title}
                            </Typography>
                          )}

                          {content.video_url && (
                            <Box
                              sx={{
                                position: 'relative',
                                width: '100%',
                                maxWidth: '600px',
                                margin: '0 auto',
                              }}
                            >
                              <ReactPlayer
                                url={content.video_url}
                                width="100%"
                                height="300px"
                                controls
                                style={{
                                  borderRadius: '8px',
                                  overflow: 'hidden',
                                }}
                              />
                            </Box>
                          )}
                          {!content.video_url && (
                            <Typography variant="body2" color="text.secondary">
                              No video URL provided
                            </Typography>
                          )}
                        </Box>
                      )}

                      {/* Document Content */}
                      {content.content_type_id === CONTENT_TYPES.document &&
                        content.document_id &&
                        content.version_id && (
                          <Box>
                            {content.title && (
                              <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ fontWeight: 600, mb: 2 }}
                              >
                                {content.title}
                              </Typography>
                            )}
                            <Paper
                              elevation={1}
                              sx={{
                                p: 3,
                                border: '1px solid',
                                borderColor: 'primary.light',
                                borderRadius: 2,
                                bgcolor: 'primary.50',
                              }}
                            >
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={2}
                              >
                                <LinkIcon color="primary" />
                                <Box sx={{ flexGrow: 1 }}>
                                  <Typography
                                    variant="body1"
                                    sx={{ fontWeight: 500, mb: 1 }}
                                  >
                                    {content.document_name ||
                                      'Document from Library'}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Click to view this document in the Library
                                  </Typography>
                                </Box>
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  component="a"
                                  href={`/library/${content.document_id}/view/${content.version_id}`}
                                  startIcon={<LinkIcon />}
                                  size="small"
                                >
                                  View Document
                                </Button>
                              </Stack>
                            </Paper>
                          </Box>
                        )}

                      {/* Bundle Content */}
                      {content.content_type_id === CONTENT_TYPES.bundle && (
                        <Box>
                          {content.title && (
                            <Typography
                              variant="h6"
                              gutterBottom
                              sx={{ fontWeight: 600, mb: 2 }}
                            >
                              {content.title}
                            </Typography>
                          )}
                          <Paper
                            elevation={1}
                            sx={{
                              p: 3,
                              border: '1px solid',
                              borderColor: 'warning.light',
                              borderRadius: 2,
                              bgcolor: 'warning.50',
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={2}
                            >
                              <LinkIcon color="warning" />
                              <Box sx={{ flexGrow: 1 }}>
                                <Typography
                                  variant="body1"
                                  sx={{ fontWeight: 500, mb: 1 }}
                                >
                                  {content.bundle_name || 'Bundle from Library'}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Click to view this bundle in the Library
                                </Typography>
                              </Box>
                              <Button
                                variant="outlined"
                                color="warning"
                                component="a"
                                href={`/library/bundles/${content.bundle_id}`}
                                startIcon={<LinkIcon />}
                                size="small"
                              >
                                View Bundle
                              </Button>
                            </Stack>
                          </Paper>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Stack>
              </CardContent>
            </Paper>
          ))}
        </Box>
      )}

      {/* Footer Actions */}
      {newsfeed.link_url && (
        <Paper
          elevation={0}
          sx={{ p: 3, borderRadius: 3, textAlign: 'center' }}
        >
          <Button
            variant="contained"
            startIcon={<LinkIcon />}
            href={newsfeed.link_url}
            target="_blank"
            rel="noopener noreferrer"
            size="large"
          >
            {linkCtaLabel}
          </Button>
        </Paper>
      )}
    </Box>
  );
}
